/**
 * WelcomePage - the tenant's public, consumer-facing landing page.
 *
 * Rendered at `<slug>.domain/welcome` (subdomain-scoped, authoritative) or the
 * legacy `/welcome/:slug` path on a generic host. Reads the welcome-page config
 * (authored by staff, stored as data on `Tenant.settings["welcome_page"]`) from
 * the public endpoint and renders an ordered list of content blocks plus
 * optional FAQ and contact sections. No auth required.
 */

import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, Loader2 } from 'lucide-react'
import { Logo } from '@/shared/components/ui/logo'
import { Button } from '@/shared/components/ui/button'
import { Separator } from '@/shared/components/ui/separator'
import { cn } from '@/shared/lib/utils'
import { buildApiUrl } from '@/shared/lib/api-origin'
import { rememberTenantSlug } from '@/consumer/lib/tenant-slug'
import { getTenantSlugFromHost } from '@/shared/lib/tenant-host'
import { applyTenantTheme, brandingToTheme } from '@/shared/lib/theme'
import { useSelfRegistrationEnabled } from '@/consumer/hooks/use-self-registration'
import {
  WelcomeBanner,
  type WelcomePageContentBlock,
} from '@/shared/components/welcome-banner'

interface WelcomePageResponse {
  organisation: {
    name: string
    slug: string
    logo_url: string | null
    primary_colour: string
    general_contact_email: string | null
  }
  welcome_page: {
    include_faqs?: boolean
    include_contact?: boolean
    content_blocks?: WelcomePageContentBlock[]
  }
  theme?: Record<string, string>
}

const FAQ_ITEMS = [
  {
    question: 'Warranties at your fingertips',
    answer:
      'No hunting for old receipts, remembering phone calls or digging through emails. Everything is in one place, accessible from anywhere, on any device, at any time.',
  },
  {
    question: 'Easy claim process',
    answer:
      'Need to make a claim? Log in to the portal where you registered your product, enter the fault details, and the process is underway.',
  },
  {
    question: 'Free to use',
    answer:
      'Experience peace of mind managing your warranties, with no service fees.',
  },
]

function useWelcomePage(slug: string | undefined) {
  return useQuery<WelcomePageResponse | null>({
    queryKey: ['welcome-page', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const res = await fetch(buildApiUrl(`/api/public/welcome/?tenant=${slug}`))
      if (!res.ok) return null
      const json = await res.json()
      if (slug) rememberTenantSlug(slug)
      return json.result ?? null
    },
    staleTime: Infinity,
  })
}

function FaqItem({
  question,
  answer,
}: {
  question: string
  answer: string
}) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-b-[0.5px] border-[var(--rule)]">
      <Button
        variant="ghost"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between rounded-none px-0 py-5 text-left"
      >
        <span className="text-[16px] font-medium text-[var(--ink)]">{question}</span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-[var(--ink-3)] transition-transform duration-150',
            open && 'rotate-180',
          )}
        />
      </Button>
      {open && (
        <p className="pb-5 text-[15px] leading-relaxed text-[var(--ink-2)]">{answer}</p>
      )}
    </div>
  )
}

export default function WelcomePage() {
  const { slug: routeSlug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  // The tenant subdomain is authoritative; the `:slug` path param is the
  // generic-host fallback for legacy `/welcome/<slug>` links.
  const slug = getTenantSlugFromHost() ?? routeSlug
  const { data, isLoading, isError } = useWelcomePage(slug)
  // WPS-138 #1: hide the register CTA on this public landing when the tenant
  // does not offer consumer self-registration (OC at go-live).
  const selfRegistrationEnabled = useSelfRegistrationEnabled(slug)

  // Apply this tenant's authored theme (if any) directly from the welcome
  // response - the page may be reached with a slug the global branding query
  // has not resolved, so it owns its own theming. Un-themed tenants inherit the
  // platform theme.
  const theme = data?.theme
  useEffect(() => {
    if (theme) applyTenantTheme(brandingToTheme({ theme }))
  }, [theme])

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--bg)]">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--ink-3)]" />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-[var(--bg)] px-6 text-center">
        <Logo size="lg" />
        <p className="text-[15px] text-[var(--ink-2)]">
          This welcome page is not available.
        </p>
      </div>
    )
  }

  const { organisation, welcome_page: welcomePage } = data
  const blocks = welcomePage.content_blocks ?? []
  const onRegister = () => navigate('/register')

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      {/* Header: tenant logo co-branded with iWarranty */}
      <header className="mx-auto flex max-w-5xl items-center justify-between px-4 py-5 sm:px-6">
        <div className="flex items-center gap-3">
          {organisation.logo_url ? (
            <img
              src={organisation.logo_url}
              alt={organisation.name}
              className="h-8 max-w-[160px] object-contain"
            />
          ) : (
            <span className="font-display text-[22px] text-[var(--ink)]">
              {organisation.name}
            </span>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate('/login')}>
          Sign in
        </Button>
      </header>

      <Separator />

      <main className="mx-auto flex max-w-5xl flex-col gap-16 px-4 py-12 sm:px-6 md:gap-24 md:py-16">
        {blocks.map((block, index) => (
          <WelcomeBanner
            key={index}
            block={block}
            orgName={organisation.name}
            onRegister={onRegister}
            registrationEnabled={selfRegistrationEnabled}
          />
        ))}

        {welcomePage.include_faqs && (
          <section className="flex flex-col gap-2">
            <h2 className="font-display mb-4 text-[28px] text-[var(--ink)] md:text-[36px]">
              Frequently asked questions
            </h2>
            {FAQ_ITEMS.map((item) => (
              <FaqItem key={item.question} {...item} />
            ))}
          </section>
        )}

        {welcomePage.include_contact && organisation.general_contact_email && (
          <section className="flex flex-col items-center gap-3 rounded-3xl bg-[var(--brand)] px-6 py-12 text-center">
            <h2 className="font-display text-[26px] text-white md:text-[32px]">
              Need a hand?
            </h2>
            <p className="text-[15px] text-white/80">
              Get in touch with the {organisation.name} team.
            </p>
            <a href={`mailto:${organisation.general_contact_email}`}>
              <Button
                size="lg"
                className="bg-white text-[var(--brand)] hover:bg-white/90"
              >
                {organisation.general_contact_email}
              </Button>
            </a>
          </section>
        )}
      </main>

      <Separator />

      <footer className="mx-auto flex max-w-5xl items-center justify-center gap-2 px-4 py-8 sm:px-6">
        <span className="text-[12px] text-[var(--ink-3)]">Powered by</span>
        <Logo size="sm" />
      </footer>
    </div>
  )
}
